<script>
  import { createEventDispatcher, onMount } from "svelte";
  import * as d3 from "d3";
  import * as dagreD3 from "dagre-d3";
  import { Session } from "svelte-session-manager";

  export let nodes = [];
  export let edges = [];
  export let width = "500px";
  export let height = "500px";
  export let selectedNode = "";
  export let zoomLevel = 100;
  export let formatEdges = (e) => e;
  export let formatNodeMetric = (e) => e;
  export let nodeMetricLabel = "weighted time";
  export let drawMode = false;
  export let confirmedNodeIds = [];
  export let confirmedEdgeKeys = [];
  export let candidateNodeIds = [];
  export let candidateEdgeKeys = [];
  export let drawStartNodeId = "";
  export let dottedBackground = false;

  const dispatch = createEventDispatcher();

  export async function downloadSVG(appName) {
    if (!plotsvg) return;

    const svgClone = plotsvg.cloneNode(true);

    const targetClasses = ["g.node", "div.nodelabel", ".node rect"];
    const styleSheets = Array.from(document.styleSheets);
    let cssText = ` 
    * {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      letter-spacing: 0.16px;
      font-size: 0.875rem;
      font-weight:400;
      line-height: 1.28572;
      line-height: 1;
    }
        svg {
    background-color: white;
  }
  `;

    styleSheets.forEach((sheet) => {
      try {
        const rules = Array.from(sheet.cssRules || sheet.rules);
        rules.forEach((rule) => {
          if (rule.type === CSSRule.STYLE_RULE) {
            const matchesTarget = targetClasses.some(
              (target) =>
                rule.selectorText && rule.selectorText.includes(target),
            );
            if (matchesTarget) cssText += rule.cssText + "\n";
          }
        });
      } catch (e) {
        // ignore CORS errors
      }
    });

    // Create <style> element with CSS
    if (cssText) {
      const styleElement = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "style",
      );
      styleElement.textContent = cssText;
      svgClone.insertBefore(styleElement, svgClone.firstChild);
    }

    // Convert images to base64
    const images = svgClone.querySelectorAll("foreignObject img");
    const imagePromises = Array.from(images).map(async (img) => {
      const src = img.src;
      if (src && !src.startsWith("data:")) {
        try {
          const response = await fetch(src);
          const blob = await response.blob();
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          img.src = base64;
        } catch (error) {
          console.warn("The image could not be converted.:", src);
        }
      }
    });

    await Promise.all(imagePromises);

    // Detect all markers dynamically
    const paths = svgClone.querySelectorAll("path[marker-end]");
    const markerIds = new Set();

    paths.forEach((path) => {
      const markerEnd = path.getAttribute("marker-end");
      if (markerEnd) {
        const match = markerEnd.match(/url\(.*#([^)]+)\)/);
        if (match && match[1].startsWith("arrowhead")) {
          markerIds.add(match[1]);
        }
      }
    });

    // Create markers for all detected marker IDs
    const defs =
      svgClone.querySelector("defs") ||
      document.createElementNS("http://www.w3.org/2000/svg", "defs");
    if (!svgClone.querySelector("defs")) {
      svgClone.insertBefore(defs, svgClone.firstChild);
    }

    markerIds.forEach((markerId) => {
      const marker = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "marker",
      );
      marker.setAttribute("id", markerId);
      marker.setAttribute("markerWidth", "10");
      marker.setAttribute("markerHeight", "7");
      marker.setAttribute("refX", "9");
      marker.setAttribute("refY", "3.5");
      marker.setAttribute("orient", "auto");

      const polygon = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "polygon",
      );
      polygon.setAttribute("points", "0 0, 10 3.5, 0 7");
      polygon.setAttribute("fill", "#1C1C20");

      marker.appendChild(polygon);
      defs.appendChild(marker);
    });

    // Clean up paths to ensure they use the correct marker-end
    paths.forEach((path) => {
      const markerEnd = path.getAttribute("marker-end");
      if (markerEnd) {
        const match = markerEnd.match(/url\(.*#([^)]+)\)/);
        if (match && match[1].startsWith("arrowhead")) {
          path.setAttribute("marker-end", `url(#${match[1]})`);
        }
      }
    });

    // Use the original SVG size
    try {
      const originalG = plotsvg.querySelector("g");
      if (originalG) {
        const bbox = originalG.getBBox();
        const padding = 20;

        const svgWidth = bbox.width + padding * 2;
        const svgHeight = bbox.height + padding * 2;

        // Adjust the cloned g element to center it
        const clonedG = svgClone.querySelector("g");
        if (clonedG) {
          // Calculate offset to center content
          const offsetX = padding - bbox.x;
          const offsetY = padding - bbox.y;

          // Apply the offset to the cloned g element
          clonedG.setAttribute(
            "transform",
            `translate(${offsetX}, ${offsetY})`,
          );
        }

        svgClone.setAttribute("width", svgWidth);
        svgClone.setAttribute("height", svgHeight);
        svgClone.setAttribute("viewBox", `0 0 ${svgWidth} ${svgHeight}`);
      }
    } catch (error) {
      console.warn("Could not get bbox of element g:", error);
      const originalWidth = plotsvg.clientWidth || 800;
      const originalHeight = plotsvg.clientHeight || 600;
      svgClone.setAttribute("width", originalWidth);
      svgClone.setAttribute("height", originalHeight);
    }
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);

    const blob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${appName}_flow-diagram.svg`;
    a.click();

    URL.revokeObjectURL(url);
  }

  let currentTransform;
  let plotsvg;
  let g;
  let zoom;
  let expandedNodeIds = new Set();

  function baseNode(id) {
    return String(id || "").split("###")[0];
  }

  function edgeKey(from, to) {
    return from + "->" + to;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function nodeDetailsHtml(node) {
    if (!expandedNodeIds.has(node.id) || !node.details?.length) return "";
    return (
      "<div class='node-detail-panel'>" +
      node.details
        .map(
          (detail) => {
            const label =
              typeof detail === "object" ? detail.label : String(detail);
            const count =
              typeof detail === "object" ? Number(detail.count) || 0 : null;
            return (
              "<div class='node-detail-row'>" +
              (count === null
                ? ""
                : "<strong class='node-detail-count'>" +
                  escapeHtml(`${count}x`) +
                  "</strong>") +
              "<span class='node-detail-line' title='" +
              escapeHtml(label) +
              "'>" +
              escapeHtml(label) +
              "</span>" +
              "</div>"
            );
          },
        )
        .join("") +
      "</div>"
    );
  }

  function appInitial(label) {
    return (
      String(label || "?")
        .trim()
        .charAt(0)
        .toUpperCase() || "?"
    );
  }

  function nodeImageLabel(node, faded = false, dark = false) {
    const initial = appInitial(node.label);
    const weightedTime = Number(node.weighted_time) || 0;
    const hasSplitWeightedTime =
      Object.prototype.hasOwnProperty.call(node, "weighted_time_before") ||
      Object.prototype.hasOwnProperty.call(node, "weighted_time_after");
    const weightedTimeBefore = Number(node.weighted_time_before) || 0;
    const weightedTimeAfter = Number(node.weighted_time_after) || 0;
    const metricHtml = hasSplitWeightedTime
      ? "<span class='app-flow-node-kpi'>before time: " +
        formatNodeMetric(weightedTimeBefore) +
        "</span><span class='app-flow-node-kpi'>after time: " +
        formatNodeMetric(weightedTimeAfter) +
        "</span>"
      : weightedTime
        ? "<span class='app-flow-node-kpi'>" +
          nodeMetricLabel +
          ": " +
          formatNodeMetric(weightedTime) +
          "</span>"
        : "";
    const image = node.image
      ? "<img src='" +
        node.image +
        "' width=28 height=28 class='app-node-image'>"
      : "";
    const fallbackClass = node.image
      ? "app-icon-fallback app-icon-fallback-hidden"
      : "app-icon-fallback";
    return (
      "<span class='app-flow-node-content" +
      (dark ? " app-flow-node-content-dark" : "") +
      "' style='opacity:" +
      (faded ? "0.28" : "1") +
      "'>" +
      "<span class='app-flow-node-icon'>" +
      "<span class='" +
      fallbackClass +
      "'>" +
      initial +
      "</span>" +
      image +
      "</span>" +
      "<span class='app-flow-node-text'>" +
      "<span class='app-flow-node-name'>" +
      node.label +
      "</span>" +
      metricHtml +
      "</span>" +
      "</span>"
    );
  }

  function applyImageFallbacks() {
    function isMissingIcon(img) {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = 24;
        canvas.height = 24;
        const context = canvas.getContext("2d");
        context.drawImage(img, 0, 0, 24, 24);
        const data = context.getImageData(0, 0, 24, 24).data;
        let coloredPixels = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          const spread =
            Math.max(data[i], data[i + 1], data[i + 2]) -
            Math.min(data[i], data[i + 1], data[i + 2]);
          if (alpha > 32 && spread > 18) coloredPixels++;
        }
        return coloredPixels < 12;
      } catch {
        return false;
      }
    }

    d3.select(plotsvg)
      .selectAll("img.app-node-image")
      .each(function () {
        const img = this;
        const showFallback = () => {
          img.style.display = "none";
          img.parentElement
            ?.querySelector(".app-icon-fallback")
            ?.classList.remove("app-icon-fallback-hidden");
        };
        img.onerror = showFallback;
        img.onload = () => {
          if (isMissingIcon(img)) showFallback();
        };
        if (img.complete && (img.naturalWidth === 0 || isMissingIcon(img)))
          showFallback();
      });
  }

  function applyDrawClasses() {
    if (!drawMode) return;
    const confirmedNodes = new Set(confirmedNodeIds);
    const candidateNodes = new Set(candidateNodeIds);
    const confirmedEdges = new Set(confirmedEdgeKeys);
    const candidateEdges = new Set(candidateEdgeKeys);

    d3.select(plotsvg)
      .selectAll("g.node")
      .classed(
        "draw_candidate",
        (d) => candidateNodes.has(d) && !confirmedNodes.has(d),
      )
      .classed("draw_confirmed", (d) => confirmedNodes.has(d));

    d3.select(plotsvg)
      .selectAll("g.edgePath")
      .classed(
        "draw_candidate",
        (d) =>
          candidateEdges.has(edgeKey(d.v, d.w)) &&
          !confirmedEdges.has(edgeKey(d.v, d.w)),
      )
      .classed("draw_confirmed", (d) => confirmedEdges.has(edgeKey(d.v, d.w)));
  }

  function drawGraph(nodes, edges) {
    g.graph().rankdir = "LR";
    // g.graph().align = "LR";

    g.nodes().forEach(function (node) {
      g.removeNode(node);
    });

    g.edges().forEach(function (edge) {
      g.removeEdge(edge);
    });

    // NODES
    const confirmedNodes = new Set(confirmedNodeIds);
    const candidateNodes = new Set(candidateNodeIds);
    const confirmedEdges = new Set(confirmedEdgeKeys);
    const candidateEdges = new Set(candidateEdgeKeys);
    const confirmedEdgeNodes = new Set();
    const candidateEdgeNodes = new Set();
    edges.forEach((edge) => {
      if (confirmedEdges.has(edgeKey(edge.from, edge.to))) {
        confirmedEdgeNodes.add(edge.from);
        confirmedEdgeNodes.add(edge.to);
      }
      if (candidateEdges.has(edgeKey(edge.from, edge.to))) {
        candidateEdgeNodes.add(edge.from);
        candidateEdgeNodes.add(edge.to);
      }
    });

    nodes.forEach(function (node) {
      const isSelected =
        node.label === selectedNode || node.id === selectedNode;
      const isConfirmed = drawMode
        ? confirmedNodes.has(node.id) || confirmedEdgeNodes.has(node.id)
        : isSelected ||
          confirmedNodes.has(node.id) ||
          confirmedEdgeNodes.has(node.id);
      const isCandidate =
        drawMode &&
        !isConfirmed &&
        (candidateNodes.has(node.id) || candidateEdgeNodes.has(node.id));
      const isDrawStart = drawMode ? node.id === drawStartNodeId : isSelected;
      const opacity = isCandidate ? 0.72 : 1;
      const tooltip = String(node.label || node.id).replace(/"/g, "&quot;");
      const escapedId = String(node.id).replace(/"/g, "&quot;");
      const hasDetails = !drawMode && Boolean(node.details?.length);
      const isDetailExpanded = expandedNodeIds.has(node.id);
      g.setNode(node.id, {
        label:
          "<div class='node-container" +
          (hasDetails ? " node-container-with-details" : "") +
          "' title=\"" +
          tooltip +
          '">' +
          (hasDetails
            ? "<button class='node-detail-toggle' data-node-id=\"" +
              escapedId +
              "\" title='" +
              (isDetailExpanded ? "Hide details" : "Show details") +
              "'>" +
              "<svg class='node-detail-toggle-symbol' viewBox='0 0 18 18' aria-hidden='true'>" +
              "<path d='M4.5 9h9'></path>" +
              (isDetailExpanded ? "" : "<path d='M9 4.5v9'></path>") +
              "</svg>" +
              "</button>"
            : "") +
          (drawMode && isConfirmed && !isDrawStart
            ? "<button class='draw-node-delete' data-node-id=\"" +
              escapedId +
              "\" title='Remove'>×</button>"
            : "") +
          "<div class='nodelabel'>" +
          nodeImageLabel(node, isCandidate, isDrawStart) +
          "</div>" +
          nodeDetailsHtml(node) +
          "</div>",
        labelType: "html",
        style: isDrawStart
          ? `fill: #1c1c20; stroke-width: 2px; stroke: #1c1c20; opacity: ${opacity};`
          : node.color
            ? `fill: ${node.color}; stroke-width: 2px; stroke: ${isSelected && !drawMode ? "#5c25fa" : "black"}; opacity: ${opacity};`
            : `fill: #f5f6f7; stroke-width: 2px; stroke: ${isSelected && !drawMode ? "#5c25fa" : "black"}; opacity: ${opacity};`,
        rx: 8,
        ry: 8,
      });
    });

    // EDGES
    // Händische Definition der Mindest- und Maximalstärke der Pfade in Pixeln
    var minWeight = 1;
    var maxWeight = 4;
    // Extrema berechnen
    var minVal = Math.min(...edges.map((e) => e.value));
    var maxVal = Math.max(...edges.map((e) => e.value));

    edges.forEach(function (edge) {
      // Strichstärke berechnen
      var weight =
        ((edge.value - minVal) / (maxVal - minVal)) * (maxWeight - minWeight) +
        minWeight;

      var color = "#1C1C20";
      const key = edgeKey(edge.from, edge.to);
      const isCandidate =
        drawMode && candidateEdges.has(key) && !confirmedEdges.has(key);
      const opacity = isCandidate ? 0.72 : 1;

      //Plot
      var minlen = edge.from == "Start" || edge.to == "End" ? 4 : 1;
      g.setEdge(edge.from, edge.to, {
        labelType: "html",
        label: `<div style="z-index: 1; background-color: rgba(255, 255, 255, 0.95); padding: 3px; opacity: ${opacity};">${formatEdges(edge.value)}</div>`,
        labelpos: "c",
        curve: d3.curveMonotoneX,
        minlen: minlen,
        style: `stroke-width: ${weight}px; stroke: ${color}; fill: none; opacity: ${opacity};`,
        arrowheadStyle: `fill: ${color}; stroke-fill: none; stroke: none; opacity: ${opacity};`,
      });
    });

    var svg = d3.select(plotsvg);
    var inner = svg.select("g");
    zoom = d3.zoom().on("zoom", ({ transform }) => {
      inner.attr("transform", transform);
      currentTransform = transform;
      zoomLevel = Math.round(transform.k * 100);
    });
    svg = svg.call(zoom);
    svg.call(zoom.transform, d3.zoomIdentity.translate(0, 0).scale(1));

    var render = new dagreD3.render();
    render(inner, g);
    applyImageFallbacks();
    applyDrawClasses();

    // Three function that change the tooltip when user hover / move / leave a cell
    var mouseover = function (d) {
      d3.select(this).select("rect").classed("node_hovered", true);
    };

    var mouseleave = function (d) {
      d3.select(this).select("rect").classed("node_hovered", false);
    };

    function selectNode(evt, d) {
      if (drawMode) {
        dispatch("nodeclick", { id: d });
        return;
      }
      selectedNode = d;
      dispatch("nodeclick", { id: d });
    }

    svg
      .selectAll("g.node")
      .on("click", selectNode)
      .on("mouseover", mouseover)
      .on("mouseleave", mouseleave);

    svg.selectAll(".draw-node-delete").on("click", function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      dispatch("nodedelete", { id: this.dataset.nodeId });
    });

    svg.selectAll(".node-detail-toggle").on("click", function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      const nodeId = this.dataset.nodeId;
      const nextExpandedNodeIds = new Set(expandedNodeIds);
      if (nextExpandedNodeIds.has(nodeId)) {
        nextExpandedNodeIds.delete(nodeId);
      } else {
        nextExpandedNodeIds.add(nodeId);
      }
      expandedNodeIds = nextExpandedNodeIds;
      redrawgraph();
    });

    const svgWidth = plotsvg.getBoundingClientRect().width;
    const svgHeight = plotsvg.getBoundingClientRect().height;
    const graphWidth = g.graph().width;
    const graphHeight = g.graph().height;

    const xOffset = (svgWidth - graphWidth) / 2;
    const yOffset = (svgHeight - graphHeight) / 2;

    svg.call(
      zoom.transform,
      d3.zoomIdentity.translate(xOffset, yOffset).scale(1),
    );
  }

  function redrawgraph() {
    g = new dagreD3.graphlib.Graph({ rankdir: "LR", align: "LR" }).setGraph({}); //setting up the graph
    g.graph().transition = function transition(selection) {
      //transition with duration 1000ms
      return selection.transition().duration(1000);
    };
    if (nodes.length > 0) return drawGraph(nodes, edges);
    else return;
  }
  onMount(redrawgraph);

  $: {
    if (plotsvg && nodes.length > 0) {
      drawMode;
      confirmedNodeIds;
      confirmedEdgeKeys;
      candidateNodeIds;
      candidateEdgeKeys;
      drawGraph(nodes, edges);
    }
  }

  function setZoomLevel() {
    if (currentTransform && zoom) {
      let x = currentTransform.x;
      let y = currentTransform.y;
      d3.select(plotsvg).call(
        zoom.transform,
        d3.zoomIdentity.translate(x, y).scale(zoomLevel / 100),
      );
    }
  }

  $: {
    if (zoomLevel) setZoomLevel();
  }

  let fullScreenDiv;
</script>

<div
  style="width:{width}; height:{height};  display:grid;"
  bind:this={fullScreenDiv}
>
  <div style="grid-row: 1; grid-column:1; height:100%">
    <svg bind:this={plotsvg} class="plotsvg" style="width:100%">
      {#if dottedBackground}
        <defs>
          <pattern id="app-flow-dot-pattern" width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#e0e0e0" />
          </pattern>
        </defs>
        <rect class="dot-background" width="100%" height="100%" fill="url(#app-flow-dot-pattern)" />
      {/if}
      <g />
    </svg>
    <slot name="extraInfo" />
    <slot name="nodeMenu" />
  </div>
</div>

<style>
  .plotsvg {
    height: 100%;
    transition: width 0.5s;
  }

  .dot-background {
    pointer-events: none;
  }

  :global(g.node) {
    cursor: pointer;
  }

  :global(div.nodelabel) {
    display: flex !important;
    align-items: center;
    padding: 0;
  }

  :global(.node-container) {
    position: relative;
  }

  :global(.draw-node-delete) {
    position: absolute;
    top: -0.55rem;
    right: -0.55rem;
    z-index: 5;
    display: flex;
    width: 1.1rem;
    height: 1.1rem;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--pax-black);
    border-radius: 50%;
    background: var(--pax-white);
    color: var(--pax-black);
    cursor: pointer;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1;
    padding: 0;
  }

  :global(.draw-node-delete:hover) {
    background: var(--pax-orange);
  }

  :global(.node-detail-toggle) {
    position: absolute;
    top: 0.45rem;
    right: 0.45rem;
    z-index: 6;
    display: grid;
    width: 18px;
    height: 18px;
    min-width: 18px;
    min-height: 18px;
    padding: 0;
    border: 1px solid #1c1c20;
    border-radius: 50%;
    place-items: center;
    background: #fff;
    color: #1c1c20;
    cursor: pointer;
    line-height: 0;
  }

  :global(.node-detail-toggle:hover) {
    border-color: #ff5f1f;
    background: #ff5f1f;
    color: #1c1c20;
  }

  :global(.node-detail-toggle-symbol) {
    display: block;
    width: 13px;
    height: 13px;
    overflow: visible;
  }

  :global(.node-detail-toggle-symbol path) {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
  }

  :global(.node-container-with-details .app-flow-node-text) {
    padding-right: 2.35rem;
  }

  :global(.node-detail-panel) {
    display: flex;
    width: 18rem;
    max-width: 18rem;
    box-sizing: border-box;
    flex-direction: column;
    gap: 0.2rem;
    padding: 7px 10px 8px 12px;
    border-top: 1px solid #1c1c20;
    border-radius: 0 0 6px 6px;
    background: #fff;
    color: var(--pax-black);
    font-size: 11px;
    line-height: 1.3;
    text-align: left;
  }

  :global(.node-detail-row) {
    display: grid;
    grid-template-columns: 2.5rem minmax(0, 1fr);
    align-items: center;
    min-height: 20px;
    gap: 0.4rem;
  }

  :global(.node-detail-count) {
    font-weight: 700;
  }

  :global(.node-detail-line) {
    display: block;
    width: 100%;
    overflow: hidden;
    cursor: default;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.app-flow-node-content) {
    display: grid;
    grid-template-columns: 3rem minmax(7rem, auto);
    align-items: center;
    min-height: 2.6rem;
  }

  :global(.app-flow-node-icon) {
    display: flex;
    position: relative;
    height: 100%;
    min-height: 2.6rem;
    align-items: center;
    justify-content: center;
    border-right: 1px solid #cfd4da;
    box-sizing: border-box;
  }

  :global(.app-flow-node-content-dark .app-flow-node-icon) {
    border-right-color: rgba(255, 255, 255, 0.3);
  }

  :global(.app-flow-node-text) {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.2rem;
    padding: 0 0.85rem;
  }

  :global(.app-node-image) {
    position: absolute;
    top: 50%;
    left: calc(50% - 0.15rem);
    z-index: 1;
    width: 1.75rem;
    height: 1.75rem;
    object-fit: contain;
    transform: translate(-50%, -50%);
  }

  :global(.app-flow-node-name) {
    display: block;
    max-width: 13rem;
    overflow: hidden;
    color: var(--pax-black);
    font-weight: 600;
    line-height: 1.15;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.app-flow-node-content-dark .app-flow-node-name) {
    color: var(--pax-white);
  }

  :global(.app-flow-node-kpi) {
    display: block;
    max-width: 13rem;
    overflow: hidden;
    color: #5f6872;
    font-size: 0.68rem;
    font-weight: 500;
    line-height: 1;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.app-flow-node-content-dark .app-flow-node-kpi) {
    color: rgba(255, 255, 255, 0.75);
  }

  :global(.app-icon-fallback) {
    display: flex;
    position: absolute;
    top: 50%;
    left: calc(50% - 0.15rem);
    width: 1.75rem;
    height: 1.75rem;
    border: 0;
    border-radius: 0.35rem;
    align-items: center;
    justify-content: center;
    background: #edf0f3;
    color: var(--pax-black);
    font-weight: 700;
    font-size: 0.85rem;
    transform: translate(-50%, -50%);
  }

  :global(.app-icon-fallback-hidden) {
    display: none;
  }

  :global(.app-flow-node-content-dark .app-icon-fallback) {
    background: rgba(255, 255, 255, 0.14);
    color: var(--pax-white);
  }

  :global(.node_hovered) {
    stroke: #ff5f1f !important;
  }

  :global(.node_selected) {
    stroke: #5c25fa !important;
  }

  :global(.draw_candidate) {
    opacity: 0.32;
  }

  :global(.draw_confirmed) {
    opacity: 1;
  }
</style>
